
import { Component, Mixins } from 'vue-property-decorator';
import {
  PermissionCode,
  AssignmentAssignee,
  AssignmentEntry,
} from '@/types';
import OperationAssignmentsDialog from '@/components/OperationAssignmentsDialog.vue';
import AssignmentEntryItem from '@/components/AssignmentEntryItem.vue';
import OperationComponentMixin from '@/mixins/OperationComponentMixin.vue';
import { getAssignmentLabel } from '@/helpers';

@Component({
  components: {
    OperationAssignmentsDialog,
    AssignmentEntryItem,
  },
})
export default class OperationAssignments extends Mixins(OperationComponentMixin) {
  editDialog = false
  editType = 'practice'

  mounted() {
    // Carica la configurazione degli assignment types se non è già presente
    if (this.$store.getters.getAssignmentTypes.length === 0) {
      this.$store.dispatch('fetchAssignmentTypes');
    }
  }

  get assignmentTypes() {
    return this.$store.getters.getAssignmentTypes;
  }

  getAssignmentTypeConfig(type: string) {
    return this.assignmentTypes.find((t: any) => t.id === type);
  }

  parseAssignees(raw: AssignmentAssignee[] | string | null | undefined): AssignmentAssignee[] {
    if (!raw) return [];
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return []; }
    }
    return raw;
  }

  buildEntry(
    type: string,
    config: any,
    childType: string,
    dateTooltip: string,
    permissionShow: string,
    permissionEdit: string,
    assigneesRaw: AssignmentAssignee[] | string | null | undefined,
    flatAssigneeId: number | null,
    flatAssigneeName: string | null,
    flatAssigned: string | null,
    flatAssignedDate: Date | null,
  ): AssignmentEntry {
    const isMulti = config.multipleAssignees === true;
    const assignees = isMulti ? this.parseAssignees(assigneesRaw) : [];

    return {
      type,
      label: getAssignmentLabel(config, childType),
      icon: config.icon || 'mdi-account',
      dateTooltip,
      permissionShow,
      permissionEdit,
      multipleAssignees: isMulti,
      assignees,
      assigneeId: flatAssigneeId,
      assigneeName: flatAssigneeName,
      assigned: isMulti ? this.aggregateStatus(assignees, flatAssigned) : flatAssigned,
      assignedDate: isMulti ? this.latestDate(assignees) : flatAssignedDate,
    };
  }

  aggregateStatus(assignees: AssignmentAssignee[], flatAssigned: string | null): string {
    if (flatAssigned === 'na') return 'na';
    if (assignees.length === 0) return 'todo';
    if (assignees.every((a) => a.status === 'done')) return 'done';
    return 'todo';
  }

  latestDate(assignees: AssignmentAssignee[]): Date | null {
    const dates = assignees
      .map((a) => a.assignedDate)
      .filter(Boolean)
      .map((d) => new Date(d as string | Date));
    if (dates.length === 0) return null;
    return dates.reduce((max, d) => (d > max ? d : max));
  }

  get entries(): AssignmentEntry[] {
    const result: AssignmentEntry[] = [];
    const child = this.operationChild;
    if (!child) return result;

    // Project per entrambe (elettrica-figlia e termica-figlia)
    const projectConfig = this.getAssignmentTypeConfig('project');
    if (['elettrica-figlia', 'termica-figlia'].includes(child.type)
      && projectConfig && this.hasPermission('projectAssignment.show')) {
      result.push(this.buildEntry(
        'project',
        projectConfig,
        child.type,
        'Data assegnazione progetto',
        'projectAssignment.show',
        'projectAssignment.edit',
        child.projectAssignees,
        child.projectAssigneeId ?? null,
        child.projectAssigneeName ?? null,
        child.projectAssigned ?? null,
        child.projectAssignedDate ?? null,
      ));
    }

    // Practice solo per elettrica-figlia
    const practiceConfig = this.getAssignmentTypeConfig('practice');
    if (child.type === 'elettrica-figlia' && practiceConfig && this.hasPermission('practiceAssignment.show')) {
      result.push(this.buildEntry(
        'practice',
        practiceConfig,
        child.type,
        'Data assegnazione pratiche',
        'practiceAssignment.show',
        'practiceAssignment.edit',
        child.practiceAssignees,
        child.practiceAssigneeId ?? null,
        child.practiceAssigneeName ?? null,
        child.practiceAssigned ?? null,
        child.practiceAssignedDate ?? null,
      ));
    }

    // ENEA per entrambe (elettrica-figlia e termica-figlia)
    const eneaConfig = this.getAssignmentTypeConfig('enea');
    if (['elettrica-figlia', 'termica-figlia'].includes(child.type)
      && eneaConfig && this.hasPermission('eneaAssignment.show')) {
      result.push(this.buildEntry(
        'enea',
        eneaConfig,
        child.type,
        'Data assegnazione ENEA/GSE/Altro',
        'eneaAssignment.show',
        'eneaAssignment.edit',
        child.eneaAssignees,
        child.eneaAssigneeId ?? null,
        child.eneaAssigneeName ?? null,
        child.eneaAssigned ?? null,
        child.eneaAssignedDate ?? null,
      ));
    }

    // Dogane solo per elettrica-figlia
    const doganeConfig = this.getAssignmentTypeConfig('dogane');
    if (child.type === 'elettrica-figlia'
      && doganeConfig && this.hasPermission('doganeAssignment.show')) {
      result.push(this.buildEntry(
        'dogane',
        doganeConfig,
        child.type,
        'Data assegnazione Dogane',
        'doganeAssignment.show',
        'doganeAssignment.edit',
        child.doganeAssignees,
        child.doganeAssigneeId ?? null,
        child.doganeAssigneeName ?? null,
        child.doganeAssigned ?? null,
        child.doganeAssignedDate ?? null,
      ));
    }

    return result;
  }

  onEdit(entry: AssignmentEntry) {
    if (!this.hasPermission(entry.permissionEdit as PermissionCode)) return;
    if (!this.isCurrentUserOwnerOrRequester) return;

    this.editType = entry.type;
    this.editDialog = true;
  }
}
